import { alpha, createTheme } from '@mui/material/styles';
// Registers MUI X Pickers' component keys (e.g. MuiPickersToolbar) on the theme's
// `components` type so styleOverrides below can target them.
import type {} from '@mui/x-date-pickers/themeAugmentation';

const muiTheme = createTheme({
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: 'Nunito,sans-serif',
    button: {
      textTransform: 'none',
    },
  },
  palette: {
    primary: {
      main: '#D83713',
    },
    secondary: {
      main: '#757575',
    },
  },
  components: {
    // MUI X Pickers v9 default toolbar (the header of the Mobile Date/Time picker
    // dialog) is unstyled white with a "SELECT DATE"/"SELECT TIME" label. Restores
    // the brand-colored header the app used before the pickers migration.
    MuiPickersToolbar: {
      styleOverrides: {
        // `title` ("SELECT DATE"/"SELECT TIME") isn't a themed styled() slot in
        // MUI X, so a top-level `title` override is a no-op - hide it via a
        // nested selector on `root` instead, which is a real themed slot.
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '& .MuiPickersToolbar-title': {
            display: 'none',
          },
        }),
      },
    },
    // The toolbar's hour/minute/AM-PM text (TimePicker) sets its own explicit
    // text.secondary/text.primary color rather than inheriting MuiPickersToolbar's
    // root color, so it needs its own override to read white against the orange header.
    MuiPickersToolbarText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: alpha(theme.palette.primary.contrastText, 0.7),
          '&[data-selected]': {
            color: theme.palette.primary.contrastText,
          },
        }),
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: ({ ownerState }) => (ownerState.container ? { width: '100%' } : {}),
      },
    },
    MuiTypography: {
      styleOverrides: {
        body2: {
          fontWeight: 300,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#FFF',
          color: '#000',
          borderRadius: 5,
          fontSize: '0.875rem',
          border: '1px solid rgba(0, 0, 0, 0.13)',
          boxShadow: '0px 10px 15px rgba(91, 78, 78, 0.15)',
          padding: 10,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: '#FFF',
          },
          backgroundColor: '#FFF',
          fontWeight: 400,
          fontSize: '0.875rem',
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#D83713',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          marginTop: 5,
          borderRadius: 5,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 300,
          '&.Mui-selected': { color: '#D83713' },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontWeight: 300,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 300,
          borderRadius: 30,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: 'Nunito,sans-serif',
          fontSize: 16,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: '#D83713',
          fontWeight: 300,
        },
        selectLabel: {
          fontWeight: 300,
        },
        displayedRows: {
          fontWeight: 300,
        },
        input: {
          fontWeight: 300,
        },
        menuItem: {
          fontWeight: 300,
          color: '#D83713',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 300,
          backgroundColor: '#fffbfa',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 300,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 300,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontWeight: 300,
          borderRadius: '10px !important',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D83713 !important',
          },
        },
        input: {
          '&::placeholder': {
            color: '#736F6FDB',
            opacity: 1,
          },
          '&:-webkit-autofill,input:-webkit-autofill:focus': {
            transition: 'background-color 600000s 0s, color 600000s 0s',
          },
          '&.MuiInputBase-inputSizeSmall:not(.MuiInputBase-inputMultiline)': {
            padding: '10.5px 14px',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: '1px solid #E0E0E0',
          boxShadow: 'none',
          borderRadius: 18,
          backgroundColor: '#fff',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        head: {
          color: 'red',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontWeight: 300,
        },
        head: {
          fontWeight: 300,
          color: '#D83713',
        },
        footer: {
          left: 0,
          bottom: 0,
          zIndex: 2,
          position: 'sticky',
          backgroundColor: '#fff',
          borderTop: '1px solid #E0E0E0',
        },
      },
    },
  },
});

export default muiTheme;
